import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Reveal } from "@/components/shared/Reveal";
import { Mail, LifeBuoy, Briefcase, ShieldCheck, Newspaper, ShieldAlert, Siren } from "lucide-react";

const CONTACTS = [
  { icon: LifeBuoy, title: "Support", desc: "General questions about your account or the RelayRigs platform.", email: "support@relayrigs.com" },
  { icon: Briefcase, title: "Business Inquiries", desc: "Partnerships, carrier programs, and enterprise conversations.", email: "support@relayrigs.com" },
  { icon: ShieldCheck, title: "Insurance / Partner Inquiries", desc: "Licensed insurance carriers and workflow integration partners.", email: "support@relayrigs.com" },
  { icon: Newspaper, title: "Media Inquiries", desc: "Press, interviews, and media resources.", email: "support@relayrigs.com" },
];

const REPORT_CONTACTS = [
  { icon: ShieldAlert, title: "Report Fraud", desc: "Fake identities, falsified credentials, fake loads, payment fraud, cargo theft, or falsified insurance.", email: "support@relayrigs.com" },
  { icon: Siren, title: "Report a Safety Concern", desc: "Unauthorized vehicle recovery, unsafe conditions, or any other safety issue on the network.", email: "support@relayrigs.com" },
];

export default function Company() {
  return (
    <div data-testid="company-page" className="min-h-screen bg-white">
      <Header />

      <section id="company-about" data-testid="company-about-section" className="pt-36 pb-20 bg-rr-navy relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center relative">
          <Reveal>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-rr-orangeLight mb-4">About RelayRigs</div>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
              Building a More Flexible Transportation Network
            </h1>
            <p className="mt-6 text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              RelayRigs is being developed to make it easier for qualified drivers, equipment owners, carriers, and authorized vehicle owners to connect when
              transportation needs another driver. The platform combines marketplace matching with operational tools for qualification, payments, HOS awareness,
              navigation, handoffs, tracking, insurance workflows, documentation, and delivery verification. RelayRigs is currently in pre-launch and building its
              founding network across the United States.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="contact" data-testid="company-contact-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-rr-orange mb-4">Contact Us</div>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-rr-navy tracking-tight">Get in Touch</h2>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {CONTACTS.map((c) => (
              <div key={c.title} data-testid={`contact-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border border-rr-line rounded-2xl p-6 hover:border-rr-orange/50 transition-colors">
                <div className="w-11 h-11 rounded-full bg-rr-surface flex items-center justify-center text-rr-navy mb-4">
                  <c.icon size={19} />
                </div>
                <h3 className="font-heading font-bold text-rr-navy">{c.title}</h3>
                <p className="text-sm text-rr-muted mt-1.5 leading-relaxed">{c.desc}</p>
                <a href={`mailto:${c.email}`} data-testid={`contact-email-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-rr-orange mt-3 hover:text-rr-navy transition-colors">
                  <Mail size={14} /> {c.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="report" data-testid="company-report-section" className="py-20 md:py-28 bg-rr-surface">
        <div className="max-w-5xl mx-auto px-5 md:px-8">
          <Reveal>
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-rr-orange mb-4">Platform Integrity</div>
            <h2 className="font-heading text-2xl md:text-3xl font-extrabold text-rr-navy tracking-tight">Report Fraud or a Safety Concern</h2>
            <p className="mt-3 text-sm text-rr-muted max-w-2xl leading-relaxed">
              Fraud, fake identities, falsified credentials, fake loads, payment fraud, cargo theft schemes, unauthorized vehicle recovery, falsified insurance, and
              other platform abuse are prohibited on RelayRigs. If you suspect any of the above, please contact us immediately.
            </p>
          </Reveal>

          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {REPORT_CONTACTS.map((c) => (
              <div key={c.title} data-testid={`report-card-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white border border-rr-line rounded-2xl p-6">
                <div className="w-11 h-11 rounded-full bg-rr-navy flex items-center justify-center text-white mb-4">
                  <c.icon size={19} />
                </div>
                <h3 className="font-heading font-bold text-rr-navy">{c.title}</h3>
                <p className="text-sm text-rr-muted mt-1.5 leading-relaxed">{c.desc}</p>
                <a href={`mailto:${c.email}`} data-testid={`report-email-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-rr-orange mt-3 hover:text-rr-navy transition-colors">
                  <Mail size={14} /> {c.email}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
